import {
  ADMIN_ROLE,
  MEMBER_ROLE,
  UDS_SCHEMAS,
} from "@/src/constants/roles-permissions.constants";
import type {
  BlocksPermission,
  BlocksPermissionsResponse,
  SwitchOrgPayload,
  UdsPolicyGetApiResponse,
  UdsPolicyGetResponse,
  UdsPolicyMapEntry,
  UdsPolicyRule,
  UpdateRolePermissionsPayload,
} from "@/src/models/permissions.model";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { BLOCKS_PERMISSION_QUERY_KEYS } from "../../query-keys/permissions.keys";
import apiClient from "../api/axios";
import { BLOCKS_ENDPOINTS } from "@/src/services/blocks/config";
import {
  fetchMyOrganizations,
  switchOrg,
  type BlocksOrganization,
} from "@/src/services/blocks/session";
import { accountStore } from "@/src/services/api/account-store";
import {
  invalidateIdentity,
  saveRefreshToken,
  saveToken,
  setActiveOrg,
  setOrgSelected,
} from "../api/auth.api";
import { Platform } from "react-native";

const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY;

/**
 * The UAM screens want every permission, and v4 has no "all" sentinel — v1's
 * `pageSize: 0` returned the lot, but a 0 page size on the v4 list contract is a
 * literal zero. `GET /iam/permissions/by-severity` reports **146** permissions in
 * this project (readable even by a permission-less session), so this is ~7x
 * headroom rather than an arbitrary large number.
 */
const ALL_PERMISSIONS_PAGE_SIZE = 1000;

/** Role slugs differ in case/separators between IAM and the app's constants. */
const normalizeRole = (role?: string | null) =>
  (role ?? "").toLowerCase().replace(/[\s_-]/g, "");

/**
 * v4 permission list — `POST /iam/v4/iam/permissions` (replaces the dead v1
 * `Iam/GetPermissions`, which now 404s `Application_Not_Found`).
 *
 * Readable since the project granted IAM access-control rights (2026-07-30), and
 * the live contract has two traps this body works around — both verified:
 *
 * 1. **`filter.search` AND `filter.isBuiltIn` are both REQUIRED.** Omitting
 *    either answers `400 {"errors":{"Filter.Search":["The Search field is
 *    required."],"Filter.IsBuiltIn":["The IsBuiltIn field is required."]}}`.
 * 2. **`isBuiltIn` is a STRING, and only `""` returns rows.** A boolean 400s
 *    ("could not be converted"); `"true"` and `"false"` both return
 *    `totalCount: 0` even though all 146 permissions in this project ARE
 *    built-in. So `""` is not "unset" here — it is the only value that means
 *    "don't filter". The skill doc's `"isBuiltIn": "false"` example is a
 *    zero-result query.
 *
 * `filter.search` does work as a substring match (`"View"` → 40 of 146).
 *
 * ⚠ The top-level `roles[]` narrowing does **not** narrow: `[]`, `["admin"]` and
 * `["clouduser"]` all return the full 146. Filter by role client-side on each
 * permission's own `roles[]` instead — see `useRoleBlocksPermissionsQuery`.
 */
const fetchBlocksPermissions = (role?: string) =>
  apiClient.post(BLOCKS_ENDPOINTS.permissions, {
    page: 0,
    pageSize: ALL_PERMISSIONS_PAGE_SIZE,
    sort: { property: "name", isDescending: false },
    filter: { search: "", isBuiltIn: "" },
    roles: role ? [role] : [],
  });

export const useAllBlocksPermissionsQuery = () =>
  useQuery<BlocksPermissionsResponse>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.allPermissions,
    queryFn: async () => (await fetchBlocksPermissions()).data,
  });

/**
 * Permissions held by one role. The server's top-level `roles[]` does not narrow
 * (verified: `["admin"]` returns all 146), so the narrowing happens here on each
 * permission's own `roles[]`, and `totalCount` is recomputed to match the rows
 * actually returned rather than echoing the unfiltered server count.
 */
export const useRoleBlocksPermissionsQuery = (role: string) =>
  useQuery<BlocksPermissionsResponse>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.permissions(role),
    queryFn: async () => {
      const body: BlocksPermissionsResponse = (
        await fetchBlocksPermissions(role)
      ).data;
      const wanted = normalizeRole(role);
      const data = (body.data ?? []).filter((permission) =>
        (permission.roles ?? []).some((r) => normalizeRole(r) === wanted),
      );
      return { ...body, data, totalCount: data.length };
    },
    enabled: !!role,
  });

/**
 * v4 grant/revoke on a role — `POST /iam/v4/iam/roles/assign-permissions`
 * (replaces the misleadingly named v1 `Iam/SetRoles`). Same semantics as v1:
 * targets the role by `slug`, adds and removes in one call, and it is a delta,
 * not a full replace. Permissions are identified by `name`. `projectKey` is gone
 * — the header carries the tenant.
 */
const updateRolePermissions = (payload: UpdateRolePermissionsPayload) =>
  apiClient.post(BLOCKS_ENDPOINTS.roleAssignPermissions, {
    slug: payload.slug,
    addPermissions: payload.addPermissions,
    removePermissions: payload.removePermissions,
  });

export const useUpdateRolePermissionsMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: updateRolePermissions,
    onSuccess: (_response, payload) => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_PERMISSION_QUERY_KEYS.permissions(payload.slug),
      });
      Toast.show({
        type: "success",
        text1: t("SUCCESS_ROLE_PERMISSIONS_UPDATED"),
      });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_ROLE_PERMISSIONS_UPDATED") });
    },
  });
};

/**
 * v4 role creation — `POST /iam/v4/iam/roles/create` (replaces v1
 * `Iam/CreateRole`). `slug` is still the stable key and is immutable after
 * creation.
 *
 * Two fields v1 had no concept of, defaulted conservatively. The route is
 * reachable now, but these were not probed by actually creating a role, so the
 * values are reasoned rather than observed:
 * - `parentRoleSlug: ""` → top-level role, which is what v1 produced (it had no
 *   hierarchy at all). Nesting a new role under an existing one would silently
 *   inherit that role's permissions.
 * - `canCreateOwn: false` and `propagateToOtherOrg: false` → least privilege, and
 *   the app is single-org per record anyway. An admin can widen either in the
 *   Cloud Portal; the reverse (discovering a role was created over-privileged) is
 *   the expensive direction.
 */
const createRole = (payload: {
  name: string;
  description?: string;
  slug: string;
}) =>
  apiClient.post(BLOCKS_ENDPOINTS.roleCreate, {
    name: payload.name,
    description: payload.description ?? "",
    slug: payload.slug,
    parentRoleSlug: "",
    propagateToOtherOrg: false,
    canCreateOwn: false,
  });

export const useCreateRoleMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_PERMISSION_QUERY_KEYS.allPermissions,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_ROLE_CREATED") });
    },
    onError: (error: any) => {
      const serverError = error?.response?.data?.errors;
      const message =
        (serverError && typeof serverError === "object"
          ? Object.values(serverError)[0]
          : serverError) ?? t("ERROR_ROLE_CREATED");
      Toast.show({ type: "error", text1: message as string });
    },
  });
};

export const useSwitchOrgMutation = () => {
  return useMutation({
    /**
     * v4 org switch — `POST /iam/v4/auth/switch-org` (see `switchOrg` in
     * blocks/session.ts). Replaces the v1 form-encoded
     * `grant_type=switch_organization` password grant.
     *
     * On web the session is the cookie: `switchOrg` rotates it and the body
     * carries no tokens, so nothing is stored in JS. On native the body returns
     * the new token pair, which is persisted below. `refreshToken` in the
     * payload is now unused for web (kept so the call sites need no change).
     */
    mutationFn: async ({ orgId }: SwitchOrgPayload) => {
      const result = await switchOrg(orgId);
      return { ...result, orgId };
    },
    onSuccess: async (data) => {
      await setActiveOrg(data.orgId);
      await setOrgSelected(true);

      if (Platform.OS === "web") {
        invalidateIdentity();
        return;
      }

      // Native: no HttpOnly cookie, so persist the rotated token pair.
      if (data.access_token) await saveToken(data.access_token);
      if (data.refresh_token) await saveRefreshToken(data.refresh_token);
      const activeAccount = await accountStore.getActive();
      if (activeAccount && data.access_token && data.refresh_token) {
        await accountStore.upsert({
          ...activeAccount,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });
      }
    },
  });
};

/**
 * v4 replacement for the dead `GET /idp/v1/Iam/GetOrganizations`.
 *
 * `GET /iam/v4/iam/organizations/my` — verified live 2026-07-30 on the session
 * cookie alone: `{organizations: [{itemId, name, createdDate}], errors,
 * isSuccess}`. The unscoped `/iam/v4/iam/organizations` list is **403** for an
 * app session, so "my orgs" is the only readable form — which is all the three
 * call sites need, since each one only resolves a name for an org the signed-in
 * user already belongs to.
 *
 * The element type is `BlocksOrganization`, not `Organization`: v4 returns three
 * fields, and `isEnable` / `lastUpdatedDate` / `language` were never read.
 * `retry: false` keeps a failure from stalling the auth context, which consumes
 * this on every load.
 */
export const useOrganizationsQuery = (options?: { enabled?: boolean }) =>
  useQuery<ReadonlyArray<BlocksOrganization>>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.organizations,
    queryFn: fetchMyOrganizations,
    enabled: options?.enabled ?? true,
    retry: false,
  });

const isRoleEnabled = (
  rules: ReadonlyArray<UdsPolicyRule>,
  role: string,
): boolean => rules.some((rule) => rule.staticValue === role);

const fetchUdsPolicyMap = async (): Promise<
  Record<string, UdsPolicyMapEntry>
> => {
  const [iamResponse, ...udsResponses] = await Promise.all([
    // Same v4 list call as the queries above — one definition of the body.
    fetchBlocksPermissions(),
    ...UDS_SCHEMAS.map((schema) =>
      apiClient.get<UdsPolicyGetApiResponse>(
        "/data/v4/data-access/policy/get",
        { params: { schemaName: schema, projectKey: PROJECT_KEY } },
      ),
    ),
  ]);

  const iamPermissions: ReadonlyArray<BlocksPermission> =
    iamResponse.data?.data ?? [];

  const udsPolicies: ReadonlyArray<UdsPolicyGetResponse> = udsResponses.flatMap(
    (res) => res.data?.data ?? [],
  );

  const udsByName = udsPolicies.reduce(
    (acc, policy) => ({ ...acc, [policy.policyName]: policy }),
    {} as Record<string, UdsPolicyGetResponse>,
  );

  return iamPermissions.reduce(
    (map, iamPermission) => {
      const udsPolicy = udsByName[iamPermission.name];
      if (!udsPolicy) return map;

      const rules = udsPolicy.ruleGroup.rules;

      return {
        ...map,
        [iamPermission.itemId]: {
          ...udsPolicy,
          adminEnabled: isRoleEnabled(rules, ADMIN_ROLE),
          memberEnabled: isRoleEnabled(rules, MEMBER_ROLE),
        } satisfies UdsPolicyMapEntry,
      };
    },
    {} as Record<string, UdsPolicyMapEntry>,
  );
};

export const useUdsPolicyMapQuery = () =>
  useQuery<Record<string, UdsPolicyMapEntry>>({
    queryKey: BLOCKS_PERMISSION_QUERY_KEYS.udsPolicyMap,
    queryFn: fetchUdsPolicyMap,
    staleTime: 5 * 60 * 1000,
  });

const reloadUdsConfigurations = (): Promise<void> =>
  apiClient.post("/data/v4/schema-configurations/reload", {});

export const useReloadUdsConfigurationsMutation = () =>
  useMutation({
    mutationFn: reloadUdsConfigurations,
  });
